import { z } from "zod";

/**
 * Kişisel güvenlik ve hesap aktivite geçmişi (CLOS-284 `[082]`).
 *
 * ### ⚠ Bu bir AUDIT ekranı DEĞİL
 *
 * Kart: *"AUDIT EKRANINDAKİ KURUMSAL VEYA TEKNİK ALANLAR AYNEN KOPYALANMAZ."* Audit
 * kurumsal bir kanıt kaydıdır ve hedef kitlesi denetçidir; burası kullanıcının kendi
 * hesabına baktığı yerdir. `correlationId`, `causationId`, ham `detail` ve olay adının
 * kendisi dışarı çıkmıyor — çıksaydı kullanıcıya iç mimarimizi okutmuş olurduk.
 *
 * ### ⚠ Yalnız KENDİ geçmişi
 *
 * Kart: *"başka kullanıcı, ORGANİZASYON YÖNETİCİSİ veya sponsor NORMAL PERMISSION ile bu
 * kişisel geçmişi açamaz."* Bu yüzden uçta tenant ya da permission parametresi YOK: sorgu
 * her zaman oturumdaki kullanıcıyla sınırlı ve başka bir kullanıcı seçmenin yolu yok.
 */

/**
 * Kullanıcıya gösterilen olay AİLELERİ.
 *
 * ⚠ Ham olay adları (`two_factor.recovery_regenerated` gibi) kullanıcıya gösterilmiyor:
 * onlar bizim iç kataloğumuz. Aile, kullanıcının zihnindeki kavram — *"giriş"*, *"parola"*,
 * *"iki adımlı doğrulama"*. Kart da olayları aileler hâlinde sayıyor.
 */
export const SECURITY_HISTORY_FAMILIES = [
  "account",
  "sign_in",
  "identity",
  "password",
  "two_factor",
  "session",
  "profile",
  "consent",
  "security",
] as const;
export type SecurityHistoryFamily = (typeof SECURITY_HISTORY_FAMILIES)[number];

export const SECURITY_HISTORY_FAMILY_NAMES: Record<SecurityHistoryFamily, string> = {
  account: "Hesap",
  sign_in: "Giriş",
  identity: "Giriş yöntemi",
  password: "Parola",
  two_factor: "İki adımlı doğrulama",
  session: "Oturum",
  profile: "Profil",
  consent: "İzinler",
  security: "Güvenlik",
};

/** Olayın SONUCU — kart: *"tarih, olay türü, cihaz/oturum özeti ve SONUÇLA gösterilir."* */
export const SECURITY_HISTORY_OUTCOMES = ["success", "failure", "info"] as const;
export type SecurityHistoryOutcome = (typeof SECURITY_HISTORY_OUTCOMES)[number];

export const SECURITY_HISTORY_OUTCOME_NAMES: Record<SecurityHistoryOutcome, string> = {
  success: "Başarılı",
  failure: "Başarısız",
  info: "Bilgi",
};

export type SecurityHistoryEntry = {
  id: string;
  /** Kullanıcının anlayacağı aile; ham olay adı DIŞARI ÇIKMIYOR. */
  family: SecurityHistoryFamily;
  /** Türkçe, tek cümlelik özet. */
  summary: string;
  outcome: SecurityHistoryOutcome;
  /** `ui` · `api` · `job` — "bunu ben mi yaptım yoksa sistem mi?" sorusunun cevabı. */
  source: string;
  /** Maskeli cihaz/oturum özeti; yoksa `null`. */
  device: string | null;
  occurredAt: string;
};

/**
 * Ham olay adı → kullanıcıya gösterilecek aile, özet ve sonuç.
 *
 * ⚠ Eşlemede OLMAYAN olay LİSTEDE GÖRÜNMÜYOR (fail-closed). Varsayılan bir "diğer" satırı
 * gösterseydik, kullanıcı anlamadığı bir teknik adla karşılaşır ve yeni eklenen her iç
 * olay farkında olmadan bu ekrana sızardı. Kart da *"yalnız ... kişisel güvenlik geçmişi
 * için İZİNLİ olay türleri gösterilir"* diyor.
 *
 * Koruma İKİ BAĞIMSIZ KATMANDA: sorgu yalnız bu eşlemenin anahtarlarını çekiyor
 * (`visibleSecurityEventNames`), ve satır yine de gelirse `securityHistoryView` `null`
 * dönüp onu düşürüyor. Diş kontrolünde her biri tek başına kaldırıldığında olay yine
 * görünmedi — yani ölçülen şey SONUÇ, tek bir katman değil.
 */
type Gorunum = { family: SecurityHistoryFamily; summary: string; outcome: SecurityHistoryOutcome };

const GORUNUM: Record<string, Gorunum> = {
  "account.registered": { family: "account", summary: "Hesap oluşturuldu", outcome: "success" },
  "account.status_changed": {
    family: "account",
    summary: "Hesap durumu değişti",
    outcome: "info",
  },
  "onboarding.completed": {
    family: "account",
    summary: "Hesap kurulumu tamamlandı",
    outcome: "success",
  },

  "email.verified": { family: "account", summary: "E-posta adresi doğrulandı", outcome: "success" },
  "email.verification_requested": {
    family: "account",
    summary: "E-posta doğrulama bağlantısı gönderildi",
    outcome: "info",
  },
  "email.change_requested": {
    family: "account",
    summary: "E-posta değişikliği talep edildi",
    outcome: "info",
  },
  "email.changed": { family: "account", summary: "Giriş e-postası değişti", outcome: "success" },
  "email.change_failed": {
    family: "account",
    summary: "E-posta değişikliği tamamlanamadı",
    outcome: "failure",
  },

  "session.signed_in": { family: "sign_in", summary: "Giriş yapıldı", outcome: "success" },
  /**
   * ⚠ Başarısız giriş kullanıcıya GÖSTERİLİYOR ve bu kartın amacı: *"olağan dışı giriş"*
   * ailesi. Kendi hesabına yapılan başarısız denemeleri görmek, kullanıcının parolasını
   * değiştirmesi gereken tek işaret olabilir.
   */
  "session.sign_in_failed": {
    family: "sign_in",
    summary: "Başarısız giriş denemesi",
    outcome: "failure",
  },
  "session.signed_out": { family: "session", summary: "Çıkış yapıldı", outcome: "info" },
  "session.revoked": { family: "session", summary: "Bir oturum sonlandırıldı", outcome: "info" },
  "session.revoked_others": {
    family: "session",
    summary: "Diğer oturumlar sonlandırıldı",
    outcome: "info",
  },

  "identity.registered": {
    family: "identity",
    summary: "Google ile hesap oluşturuldu",
    outcome: "success",
  },
  "identity.linked": {
    family: "identity",
    summary: "Hesaba Google giriş yöntemi eklendi",
    outcome: "success",
  },
  "identity.signed_in": {
    family: "sign_in",
    summary: "Google ile giriş yapıldı",
    outcome: "success",
  },
  "identity.link_rejected": {
    family: "identity",
    summary: "Giriş yöntemi ekleme reddedildi",
    outcome: "failure",
  },

  "password.changed": { family: "password", summary: "Parola değiştirildi", outcome: "success" },
  "password.reset_requested": {
    family: "password",
    summary: "Parola sıfırlama talep edildi",
    outcome: "info",
  },
  "password.reset": { family: "password", summary: "Parola sıfırlandı", outcome: "success" },
  "password.reset_failed": {
    family: "password",
    summary: "Parola sıfırlama tamamlanamadı",
    outcome: "failure",
  },

  "two_factor.setup_started": {
    family: "two_factor",
    summary: "İki adımlı doğrulama kurulumu başlatıldı",
    outcome: "info",
  },
  "two_factor.enabled": {
    family: "two_factor",
    summary: "İki adımlı doğrulama açıldı",
    outcome: "success",
  },
  "two_factor.disabled": {
    family: "two_factor",
    summary: "İki adımlı doğrulama kapatıldı",
    outcome: "info",
  },
  "two_factor.failed": {
    family: "two_factor",
    summary: "İki adımlı doğrulama kodu hatalı",
    outcome: "failure",
  },
  "two_factor.recovery_used": {
    family: "two_factor",
    summary: "Kurtarma kodu kullanıldı",
    outcome: "info",
  },
  "two_factor.recovery_regenerated": {
    family: "two_factor",
    summary: "Kurtarma kodları yenilendi",
    outcome: "info",
  },

  "profile.updated": {
    family: "profile",
    summary: "Profil bilgileri güncellendi",
    outcome: "info",
  },
  "consent.granted": { family: "consent", summary: "İzin verildi", outcome: "info" },
  "consent.withdrawn": { family: "consent", summary: "İzin geri çekildi", outcome: "info" },
  "permission.denied": {
    family: "security",
    summary: "Yetkisiz bir işlem denendi",
    outcome: "failure",
  },
};

export function securityHistoryView(eventName: string): Gorunum | null {
  return GORUNUM[eventName] ?? null;
}

/** Kullanıcıya gösterilen olay adları — sorgu bu kümeyle sınırlanıyor. */
export function visibleSecurityEventNames(): string[] {
  return Object.keys(GORUNUM);
}

/**
 * Sayfalama girdisi — imleç tabanlı.
 *
 * ⚠ Sayfa numarası DEĞİL imleç: güvenlik geçmişi sürekli büyüyor ve numaralı sayfalamada
 * kullanıcı 2. sayfaya geçtiğinde arada yeni bir olay doğarsa bir satırı hiç görmezdi.
 */
export const securityHistoryQuerySchema = z.object({
  /**
   * Sayfalama imleci — OPAK BİLEŞİK (`<zaman>|<kimlik>`), düz tarih DEĞİL.
   *
   * ⚠ Önceden ISO tarihiydi ve **satır düşürüyordu**: Postgres `timestamptz` MİKROsaniye
   * tutuyor, `Date.toISOString()` MİLİsaniyeye kırpıyor; azalan sırada kırpılmış imleç
   * gerçek değerden küçük kalıyor ve aradaki olaylar `<` koşuluna hiç girmiyor.
   *
   * Bu tablo için kusur TEORİK DEĞİLDİ: `created_at` `defaultNow()` ile yazılıyor ve
   * ölçüldüğünde 15.119 satırın 15.093'ü mikrosaniye taşıyordu. Kullanıcı kendi güvenlik
   * geçmişinde sayfa atladığında olayları göremiyordu.
   *
   * Kimlik ikinci anahtar olarak duruyor ki eşit zamanlı olaylar da atlanmasın. İstemci
   * imleci ÇÖZMEZ, olduğu gibi geri gönderir.
   */
  cursor: z
    .string()
    .regex(/^[^|]{1,64}\|[0-9a-f-]{36}$/, "Geçersiz sayfa imleci")
    .optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  family: z.enum(SECURITY_HISTORY_FAMILIES).optional(),
});
export type SecurityHistoryQuery = z.infer<typeof securityHistoryQuerySchema>;

export type SecurityHistoryPage = {
  items: SecurityHistoryEntry[];
  /** Bir sonraki sayfanın imleci; yoksa `null`. */
  nextCursor: string | null;
};
